import WarningIcon from '@mui/icons-material/WarningAmber';
import {styled} from '@mui/material';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import {useQuery} from '@tanstack/react-query';
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import CustomAlert from '../../../components/CustomAlert/CustomAlert';
import CustomButtonGroup from '../../../components/CustomButtonGroup/CustomButtonGroup';
import CustomDialog from '../../../components/CustomDialog/CustomDialog';
import CustomAutoComplete from '../../../components/CustomFormFields/CustomAutoComplete/CustomAutoComplete';
import {ConfigConstant} from '../../../constants/config';
import getCommonFilterValues from '../../../service/getCommonFilters';
import {bulkOutletSync} from '../../../service/Outlet';
import {pxToRem} from '../../../utils';

const StyledFormContainer = styled('div')(({theme}) => ({
  fontSize: '12px',
  boxShadow: theme.palette.boxShadow.type4,
  tableLayout: 'fixed',
  backgroundColor: theme.palette.white,
  color: theme.palette.black,
  width: '100%',
  borderRadius: pxToRem(4),
  padding: pxToRem(8),
  overflowX: 'auto',
}));

const StyledCustomButtonGroup = styled(CustomButtonGroup)(({theme}) => ({
  display: 'block',
  marginLeft: theme.spacing(1),
  textAlign: 'right',
  '& .custom-button': {
    minWidth: 100,
  },
}));

const StyledWarningIcon = styled(WarningIcon)(({theme}) => ({
  color: theme.palette.warning.main,
}));

const BulkOutletSyncForm = ({refetchSyncHistory}) => {
  const {email} = useSelector((state) => state.auth);
  const commonFilters = useSelector((store) => store?.commonFilters);
  const [isLoading, setIsLoading] = useState(false);
  const [alertData, setAlertData] = useState({isOpen: false, text: '', severity: 'info'});
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [channel, setChannel] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState([]);
  const [physicalStore, setPhysicalStore] = useState([]);
  const getOptions = () => {
    return {
      channel: commonFilters.channelCode || [],
      country: commonFilters.countryCode || [],
      city: [{label: 'ALL', code: 'ALL'}, ...(commonFilters.city?.filter((option) => option.code !== 'NA') || [])],
      partnerPhyStore: [
        {label: 'ALL', code: 'ALL'},
        ...(commonFilters.partnerPhyStoreCode?.filter((option) => option.code !== 'NA') || []),
      ],
      partnerStore: [
        {label: 'ALL', code: 'ALL'},
        ...(commonFilters.partnerStoreCode?.filter((option) => option.code !== 'NA') || []),
      ],
      partnerBrand: [
        {label: 'ALL', code: 'ALL'},
        ...(commonFilters.partnerBrandCode?.filter((option) => option.code !== 'NA') || []),
      ],
    };
  };
  const getAllOption = async ({country = '', cityList = [], storeList = []}) => {
    if (cityList.length === 1 && cityList[0] === 'ALL') return {partnerPhyStoreCode: getOptions().partnerPhyStore};
    if (storeList.length === 1 && storeList[0] === 'ALL') return {partnerStoreCode: getOptions().partnerStore};
    return getCommonFilterValues({
      country,
      cityList,
      storeList,
    });
  };
  useEffect(() => {
    setFormData((prevState) => {
      const updatedFormData = {};
      Object.keys(prevState).forEach((key) => {
        updatedFormData[key] = {
          ...prevState[key],
          options: getOptions()[key],
        };
      });
      return updatedFormData;
    });
  }, [commonFilters]);
  const [formData, setFormData] = useState({
    channel: {
      label: 'Channel',
      value: null,
      options: getOptions().channel,
      multiple: false,
      hide: false,
      disabled: false,
      error: false,
    },
    country: {
      label: 'Country',
      value: null,
      options: getOptions().country,
      multiple: false,
      hide: false,
      disabled: false,
      error: false,
    },
    city: {
      label: 'City',
      value: [{label: 'ALL', code: 'ALL'}],
      options: getOptions().city,
      multiple: true,
      hide: false,
      disabled: true,
      error: false,
    },
    partnerBrand: {
      label: 'Brand',
      value: [{label: 'ALL', code: 'ALL'}],
      options: getOptions().partnerBrand,
      multiple: true,
      hide: false,
      disabled: true,
      error: false,
    },
    partnerPhyStore: {
      label: 'Store',
      value: [{label: 'ALL', code: 'ALL'}],
      options: getOptions().partnerPhyStore,
      multiple: true,
      hide: false,
      disabled: true,
      error: false,
    },
    partnerStore: {
      label: 'Virtual Store Code',
      value: [{label: 'ALL', code: 'ALL'}],
      options: getOptions().partnerStore,
      multiple: true,
      hide: false,
      disabled: true,
      error: false,
    },
  });

  const dataQueryFetchCityAndBrandList = useQuery({
    queryKey: ['data-city-and-brand', {country}],
    queryFn: () => getAllOption({country}),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (
      dataQueryFetchCityAndBrandList.isSuccess &&
      (dataQueryFetchCityAndBrandList.data?.city || dataQueryFetchCityAndBrandList.data?.partnerBrandCode)
    ) {
      setFormData((prevState) => {
        const updatedFormData = {};
        Object.keys(prevState).forEach((key) => {
          updatedFormData[key] = {
            ...prevState[key],
            ...(key === 'city' && {options: dataQueryFetchCityAndBrandList.data?.city}),
            ...(key === 'city' && {value: [{label: 'ALL', code: 'ALL'}]}),
            ...(key === 'partnerBrand' && {options: dataQueryFetchCityAndBrandList.data?.partnerBrandCode}),
            ...(key === 'partnerBrand' && {value: [{label: 'ALL', code: 'ALL'}]}),
          };
        });
        return updatedFormData;
      });
      setCity(['ALL']);
    }
    setIsLoading(false);
  }, [dataQueryFetchCityAndBrandList.data]);

  const dataQueryFetchStoreList = useQuery({
    queryKey: ['data-physicalStore', {city}],
    queryFn: () => getAllOption({cityList: city}),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (dataQueryFetchStoreList.isSuccess && dataQueryFetchStoreList.data?.partnerPhyStoreCode) {
      setFormData((prevState) => {
        const updatedFormData = {};
        Object.keys(prevState).forEach((key) => {
          let newValue;
          if (key === 'partnerPhyStore') {
            newValue = prevState[key].value.filter((val) =>
              dataQueryFetchStoreList.data?.partnerPhyStoreCode?.some((option) => option.code === val.code)
            );
            newValue = newValue?.length ? newValue : [{label: 'ALL', code: 'ALL'}];
            setPhysicalStore(newValue?.map((val) => val.code));
          }
          updatedFormData[key] = {
            ...prevState[key],
            ...(key === 'partnerPhyStore' && {options: dataQueryFetchStoreList.data?.partnerPhyStoreCode}),
            ...(key === 'partnerPhyStore' && {value: newValue}),
          };
        });
        return updatedFormData;
      });
    }
    setIsLoading(false);
  }, [dataQueryFetchStoreList.data]);

  const dataQueryFetchVirtualStoreList = useQuery({
    queryKey: ['data-virtualStore', {physicalStore}],
    queryFn: () => getAllOption({storeList: physicalStore}),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (dataQueryFetchVirtualStoreList.isSuccess && dataQueryFetchVirtualStoreList.data?.partnerStoreCode) {
      setFormData((prevState) => {
        const updatedFormData = {};
        Object.keys(prevState).forEach((key) => {
          let newValue;
          if (key === 'partnerStore') {
            newValue = prevState[key].value.filter((val) =>
              dataQueryFetchVirtualStoreList.data?.partnerStoreCode?.some((option) => option.code === val.code)
            );
            newValue = newValue?.length ? newValue : [{label: 'ALL', code: 'ALL'}];
          }
          updatedFormData[key] = {
            ...prevState[key],
            ...(key === 'partnerStore' && {options: dataQueryFetchVirtualStoreList.data?.partnerStoreCode}),
            ...(key === 'partnerStore' && {value: newValue}),
          };
        });
        return updatedFormData;
      });
    }
    setIsLoading(false);
  }, [dataQueryFetchVirtualStoreList.data]);

  const validateFormData = () => {
    const formErrors = [];
    Object.keys(formData).forEach((key) => {
      if (key === 'channel' || key === 'country') {
        if (!formData[key].value) formErrors.push({id: key, message: 'Required'});
      }
    });
    const isValid = formErrors.length === 0;
    return {isValid, formErrors};
  };

  const setFormErrors = ({formErrors}) => {
    if (Array.isArray(formErrors) && formErrors.length > 0) {
      setFormData((prevState) => {
        const updatedFormData = {};
        Object.keys(prevState).forEach((key) => {
          const data = prevState[key];
          const error = formErrors.find((err) => err.id === key);
          if (error) {
            updatedFormData[key] = {
              ...data,
              error: true,
              helperText: error.message || '',
            };
          } else {
            updatedFormData[key] = data;
          }
        });
        return updatedFormData;
      });
    }
  };

  useEffect(() => {
    const timeId = setTimeout(() => {
      alertData.isOpen && closeAlert();
    }, 5000);
    return () => {
      clearTimeout(timeId);
    };
  }, [alertData.isOpen]);

  const handleOnChangeFormFields = (id, newValue) => {
    // enable other fields after selecting both channel and country
    if ((channel && id === 'country') || (country && id === 'channel')) {
      setFormData((prevState) => {
        const updatedFormData = {};
        Object.keys(prevState).forEach((key) => {
          updatedFormData[key] = {
            ...prevState[key],
            disabled: key !== 'country' && key !== 'channel' && !newValue?.code ? true : false,
          };
        });
        return updatedFormData;
      });
    }
    if (id === 'country') {
      setCountry(newValue?.code || '');
      if (newValue?.code) setIsLoading(true);
    } else if (id === 'channel') {
      setChannel(newValue?.code || '');
    } else if (id === 'city') {
      if (newValue?.length) setIsLoading(true);
      const newCityList =
        newValue?.length && newValue[newValue?.length - 1].code === 'ALL'
          ? ['ALL']
          : newValue?.map((item) => item.code);
      setCity(newCityList);
    } else if (id === 'partnerPhyStore') {
      if (newValue?.length) setIsLoading(true);
      const newPhyStoreList =
        newValue?.length && newValue[newValue?.length - 1].code === 'ALL'
          ? ['ALL']
          : newValue?.map((item) => item.code);
      setPhysicalStore(newPhyStoreList);
    }
    // remove other values if ALL selected & v.v
    if (id !== 'channel' && id !== 'country') {
      newValue =
        newValue[newValue?.length - 1]?.code === 'ALL' || !newValue?.length
          ? [{label: 'ALL', code: 'ALL'}]
          : newValue?.length > 1
            ? newValue?.filter((val) => val?.code !== 'ALL')
            : newValue;
    }
    setFormData((prevFormData) => {
      const newState = {
        ...prevFormData,
        [id]: {
          ...prevFormData[id],
          value: newValue,
          error: false,
          helperText: '',
        },
      };
      return newState;
    });
  };

  const createDataToSend = () => {
    const dataToSend = {
      operationType: 'BULK_STORE_STATUS_SYNC',
      client: 'phoenix',
      user: email,
      partnerCode: ConfigConstant.CODE_REBELPLUS,
    };
    Object.keys(formData).forEach((key) => {
      const selectedValue = Array.isArray(formData[key]?.value)
        ? formData[key]?.value?.filter((val) => val.code !== 'ALL')
        : formData[key]?.value;
      if (key === 'channel') {
        const [channelGroupCode, channelCode] = selectedValue?.code?.split('-') || [];
        const [channelGroupName, channelName] = selectedValue?.label?.split('-') || [];
        dataToSend.channelName = channelName === 'direct' ? channelGroupName : channelName;
        dataToSend.channelGroupName = channelGroupName;
        dataToSend.channelCode = channelCode;
        dataToSend.channelGroupCode = channelGroupCode;
      } else if (key === 'country') {
        dataToSend['countryCode'] = selectedValue?.code;
        dataToSend['countryName'] = selectedValue?.label;
      } else if (key === 'city') {
        dataToSend['cityCode'] = selectedValue.map((val) => val.code);
        dataToSend['cityName'] = selectedValue.map((val) => val.label);
      } else if (key === 'partnerPhyStore') {
        dataToSend['partnerPhyStoreCode'] = selectedValue.map((val) => val.code);
        dataToSend['partnerPhyStoreName'] = selectedValue.map((val) => val.label);
      } else if (key === 'partnerStore') {
        dataToSend['partnerStoreCode'] = selectedValue.map((val) => val.code);
        dataToSend['partnerStoreName'] = selectedValue.map((val) => val.label);
      } else if (key === 'partnerBrand') {
        dataToSend['partnerBrandCode'] = selectedValue.map((val) => val.code);
        dataToSend['partnerBrandName'] = selectedValue.map((val) => val.label);
      }
    });
    return dataToSend;
  };

  const handleConfirmDialogOpen = () => {
    const {isValid, formErrors} = validateFormData();
    if (!isValid) {
      return setFormErrors({formErrors});
    }
    setOpenConfirmDialog(true);
  };

  const handleConfirmDialogClose = () => {
    setOpenConfirmDialog(false);
  };

  const handleBulkSync = async () => {
    const dataToSend = createDataToSend();
    const response = await bulkOutletSync(dataToSend);
    setOpenConfirmDialog(false);
    setAlertData({
      isOpen: true,
      text: response?.success
        ? `Your request has been submitted. Status will be updated once processing is complete`
        : JSON.stringify(response?.data?.message) || 'Oops! Something went wrong',
      severity: response.success ? 'info' : 'error',
    });
    clearForm();
    refetchSyncHistory();
  };

  const clearForm = async () => {
    setCountry('');
    setCity(['ALL']);
    setPhysicalStore(['ALL']);

    setFormData((prevState) => {
      const updatedFormData = {};
      Object.keys(prevState).forEach((key) => {
        updatedFormData[key] = {
          ...prevState[key],
          value: prevState[key]?.multiple ? [{label: 'ALL', code: 'ALL'}] : null,
          disabled: key === 'country' || key === 'channel' ? false : true,
        };
      });
      return updatedFormData;
    });
  };

  const closeAlert = () => {
    setAlertData((prevState) => ({
      ...prevState,
      isOpen: false,
    }));
  };
  return (
    <StyledFormContainer>
      {isLoading && <LinearProgress />}
      <CustomAlert
        isOpen={alertData.isOpen}
        closeAlert={closeAlert}
        text={alertData.text}
        severity={alertData.severity}
      />
      <Box sx={{p: 1}}>
        {Object.keys(formData)?.map((key, index) => {
          return (
            <CustomAutoComplete
              key={key}
              id={key}
              multiple={formData[key].multiple}
              label={formData[key].label}
              disabled={formData[key].disabled}
              options={formData[key].options}
              value={formData[key]?.value}
              filterSelectedOptions={true}
              onChange={handleOnChangeFormFields}
              error={formData[key]?.error}
              helperText={formData[key].helperText || ''}
            />
          );
        })}
      </Box>

      <Box sx={{display: 'flex', justifyContent: 'end', paddingRight: 1.1}}>
        <StyledCustomButtonGroup
          first={{
            text: 'Clear',
            color: 'primary',
            variant: 'outlined',
            onClick: clearForm,
            size: 'medium',
            hide: false,
          }}
          second={{
            text: 'Sync',
            color: 'primary',
            variant: 'contained',
            size: 'medium',
            hide: false,
            isLoading,
            onClick: handleConfirmDialogOpen,
          }}
        />
      </Box>
      <CustomDialog
        open={openConfirmDialog}
        toggleOff={handleConfirmDialogClose}
        dialogTitle={'Confirmation'}
        dialogContent={'Your action may affect a large number of stores. Are you sure you want to proceed?'}
        DialogIcon={StyledWarningIcon}
        actionButtons={[
          {
            text: 'No',
            color: 'primary',
            onClick: handleConfirmDialogClose,
            variant: 'outlined',
          },
          {
            text: 'Yes',
            color: 'primary',
            onClick: handleBulkSync,
            variant: 'contained',
          },
        ]}
      />
    </StyledFormContainer>
  );
};

export default BulkOutletSyncForm;
